import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	Icon,
	IconButton,
	Modal,
	Radio,
	RadioGroup,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { ProvisionService } from "../../../../shared/services/api/provisoes/ProvisionService";

interface Props {
	open: boolean;
	borderoId: string;
	onClose: () => void;
	onSuccess: () => void;
}

// Cancelar borderô (item 6 + item 13) — o motivo + o tipo (erro/real) determinam, no backend, se
// os débitos lançados voltam a ficar disponíveis pra um novo borderô ("erro") ou ficam mesmo
// cancelados junto ("real"). Ver GAPS_BORDERO_PARA_BACKEND.md.
export const ModalCancelBorderoSinister = ({ open, borderoId, onClose, onSuccess }: Props) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();

	const [reason, setReason] = useState("");
	const [cancelType, setCancelType] = useState<"erro" | "real">("real");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setReason("");
			setCancelType("real");
			setError(null);
		}
	}, [open]);

	const handleConfirm = async () => {
		if (!reason.trim()) {
			setError("Informe o motivo do cancelamento.");
			return;
		}
		setError(null);
		setIsLoading(true);
		const result = await ProvisionService.borderoCancel(borderoId, { canceledReason: reason, cancelType });
		setIsLoading(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", position: "top-right", messageOrContent: <ToastContent title="Erro" content={result.message} /> });
			return;
		}

		toastHandler.present({
			type: "success",
			position: "top-right",
			messageOrContent: <ToastContent title="Sucesso" content="Borderô cancelado." />,
		});
		onSuccess();
		onClose();
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 560,
					borderRadius: 3,
				}}
			>
				<LoadingComp isopen={isLoading} />

				<Grid container justifyContent="space-between" alignItems="center">
					<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
						Cancelar Borderô
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Grid>

				<Divider sx={{ my: 2 }} />

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
					O cancelamento foi:
				</Typography>
				<FormControl sx={{ mb: 2 }}>
					<RadioGroup row value={cancelType} onChange={(e) => setCancelType(e.target.value as "erro" | "real")}>
						<FormControlLabel value="erro" control={<Radio />} label="Erro (engano ao gerar)" />
						<FormControlLabel value="real" control={<Radio />} label="Cancelamento real" />
					</RadioGroup>
				</FormControl>

				<TextField fullWidth required label="Motivo" multiline minRows={3} value={reason} onChange={(e) => setReason(e.target.value)} />

				<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
					<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
						Voltar
					</Button>
					<Button color="error" variant="contained" startIcon={<Icon>cancel</Icon>} onClick={handleConfirm} sx={{ minWidth: 140 }}>
						Cancelar borderô
					</Button>
				</Grid>
			</Box>
		</Modal>
	);
};
